import { phoneNumber } from '../shared/patient-normalization'
import type { RawDashboardAppointment } from './appointments-contracts'

export type AppointmentView = 'upcoming' | 'past'

export type AppointmentItem = {
  appointmentId: string
  patientAuthorityId: string | null
  patientDisplayName: string
  patientPhone: string | null
  scheduledAtMs: number | null
  scheduledEndMs: number | null
  scheduledWallTime: string | null
  clinicTimeZone: string | null
  appointmentTypeName: string | null
  providerName: string | null
  operatoryName: string | null
  source: RawDashboardAppointment['source']
  sourceLabel:
    | 'Booked by Flio · SMS'
    | 'Web Widget'
    | 'Booked by Flio · Voice'
    | 'Booked by Flio · Dashboard'
    | 'Practice system'
  status: RawDashboardAppointment['status']
  statusLabel:
    | 'Scheduled'
    | 'Confirmed'
    | 'Cancelled'
    | 'Cancelled for rescheduling'
    | 'Status unavailable'
  pmsState: RawDashboardAppointment['pmsState']
  bookedAtMs: number | null
  confirmedAtMs: number | null
  cancelledAtMs: number | null
  cancellationReason: string | null
  temporalConfidence: RawDashboardAppointment['temporalConfidence']
}

export type FormattedAppointmentTime = {
  text: string
  timeZoneLabel: string
  usedUtcFallback: boolean
}

function clean(value: string | null): string | null {
  const result = value?.trim()
  return result || null
}

export function normalizeAppointment(
  raw: RawDashboardAppointment
): AppointmentItem {
  const cancellationReason = clean(raw.cancellationReason)
  return {
    appointmentId: raw.appointmentId,
    patientAuthorityId: clean(raw.patientAuthorityId),
    patientDisplayName: clean(raw.patientDisplayName) ?? 'Patient',
    patientPhone: phoneNumber(raw.patientPhone),
    scheduledAtMs: raw.scheduledAtMs,
    scheduledEndMs: raw.scheduledEndMs,
    scheduledWallTime: clean(raw.scheduledWallTime),
    clinicTimeZone: clean(raw.clinicTimeZone),
    appointmentTypeName: clean(raw.appointmentTypeName),
    providerName: clean(raw.providerName),
    operatoryName: clean(raw.operatoryName),
    source: raw.source,
    sourceLabel:
      raw.source === 'sms'
        ? 'Booked by Flio · SMS'
        : raw.source === 'widget'
          ? 'Web Widget'
          : raw.source === 'voice'
            ? 'Booked by Flio · Voice'
            : raw.source === 'dashboard'
              ? 'Booked by Flio · Dashboard'
              : 'Practice system',
    status: raw.status,
    statusLabel:
      raw.status === 'scheduled'
        ? 'Scheduled'
        : raw.status === 'confirmed'
          ? 'Confirmed'
          : raw.status === 'cancelled' &&
              cancellationReason?.toLowerCase() === 'reschedule'
            ? 'Cancelled for rescheduling'
            : raw.status === 'cancelled'
              ? 'Cancelled'
              : 'Status unavailable',
    pmsState: raw.pmsState,
    bookedAtMs: raw.bookedAtMs,
    confirmedAtMs: raw.confirmedAtMs,
    cancelledAtMs: raw.cancelledAtMs,
    cancellationReason,
    temporalConfidence: raw.temporalConfidence,
  }
}

// A Flio booking NexHealth holds but the clinic's practice system has not
// confirmed this long after booking needs a staff check, not a new booking.
export const PMS_ATTENTION_AFTER_MS = 60 * 60 * 1000

export type PmsPresentation = {
  label: 'In clinic system' | 'Awaiting clinic system' | 'Check clinic system'
  needsAttention: boolean
}

export function pmsPresentation(
  appointment: AppointmentItem,
  nowMs: number
): PmsPresentation | null {
  if (
    appointment.pmsState === null ||
    (appointment.status !== 'scheduled' && appointment.status !== 'confirmed')
  ) {
    return null
  }
  if (appointment.pmsState === 'confirmed') {
    return { label: 'In clinic system', needsAttention: false }
  }
  const needsAttention =
    appointment.bookedAtMs !== null &&
    nowMs - appointment.bookedAtMs >= PMS_ATTENTION_AFTER_MS
  return {
    label: needsAttention ? 'Check clinic system' : 'Awaiting clinic system',
    needsAttention,
  }
}

function validTimeZone(value: string | null): string | null {
  if (!value) return null
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value }).format(0)
    return value
  } catch {
    return null
  }
}

export function appointmentTimeZoneContext(
  appointments: AppointmentItem[]
): string {
  const zones = new Set(
    appointments.flatMap((appointment) => {
      if (appointment.scheduledAtMs === null) return []
      return [
        formatAppointmentTime(
          appointment.scheduledAtMs,
          appointment.clinicTimeZone
        ).timeZoneLabel,
      ]
    })
  )
  if (zones.size === 1) return `Times shown in ${Array.from(zones)[0]}`
  if (zones.size > 1) return 'Times shown in each recorded clinic timezone'
  return 'Timezone shown when an exact appointment time is available'
}

export function formatAppointmentRowTime(
  timestamp: number,
  clinicTimeZone: string | null
): string {
  const timeZone = validTimeZone(clinicTimeZone) ?? 'UTC'
  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(timestamp)
}

export function formatAppointmentTime(
  timestamp: number,
  clinicTimeZone: string | null
): FormattedAppointmentTime {
  const timeZone = validTimeZone(clinicTimeZone) ?? 'UTC'
  return {
    text: new Intl.DateTimeFormat('en-US', {
      timeZone,
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(timestamp),
    timeZoneLabel: timeZone,
    usedUtcFallback: timeZone === 'UTC' && clinicTimeZone !== 'UTC',
  }
}

function timeZoneName(timestamp: number, timeZone: string) {
  return (
    new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' })
      .formatToParts(timestamp)
      .find((part) => part.type === 'timeZoneName')?.value ?? timeZone
  )
}

/** The row's two lines: the day, then the start time (clinic time). */
export function formatAppointmentRowParts(
  timestamp: number,
  clinicTimeZone: string | null,
  nowMs: number
) {
  const timeZone = validTimeZone(clinicTimeZone) ?? 'UTC'
  const year = (value: number) =>
    new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric' }).format(
      value
    )
  return {
    day: new Intl.DateTimeFormat('en-US', {
      timeZone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      ...(year(timestamp) !== year(nowMs) ? { year: 'numeric' } : {}),
    }).format(timestamp),
    time: new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour: 'numeric',
      minute: '2-digit',
    }).format(timestamp),
  }
}

/** The detail summary: full day, start–end range and the clinic's zone. */
export function formatAppointmentSchedule(appointment: AppointmentItem) {
  if (appointment.scheduledAtMs === null) return null
  const start = appointment.scheduledAtMs
  const end = appointment.scheduledEndMs
  const timeZone = validTimeZone(appointment.clinicTimeZone) ?? 'UTC'
  const time = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
  })
  return {
    date: new Intl.DateTimeFormat('en-US', {
      timeZone,
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }).format(start),
    time:
      end !== null && end > start
        ? time.formatRange(start, end)
        : time.format(start),
    timeZone: timeZoneName(start, timeZone),
  }
}
